import React, { useState, useEffect } from 'react';
import config from '../../config';
import { useSelector } from 'react-redux';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import './MainCenterGaugeChart.css';
import Container from 'react-bootstrap/Container';
import { formatPower } from '../../public/formatPower'; // 유틸리티 함수 불러오기
import useIsMobile from '../../hooks/useIsMobile';
import IvCurrentPowerPanel from '../../components/IvCurrentPowerPanel';

ChartJS.register(ArcElement, Tooltip, Legend);

/** 목업 게이지: Minimum(파랑) → Maximum(연두) */
const GAUGE_GRADIENT = {
  start: '#0075FF',
  mid: '#2AB09C',
  end: '#82CD3F',
  accent: '#0075FF',
  needle: '#5a5c61',
};

function createGaugeArcGradient(ctx, chartArea) {
  const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
  gradient.addColorStop(0, GAUGE_GRADIENT.start);
  gradient.addColorStop(0.5, GAUGE_GRADIENT.mid);
  gradient.addColorStop(1, GAUGE_GRADIENT.end);
  return gradient;
}

const needlePlugin = {
  id: 'needle',
  afterDraw(chart) {
    const { ctx, chartArea: { top, bottom, left, right, height } } = chart;
    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 1.21;
    const raw = Number(chart.config.options.needleValue);
    const value = Number.isFinite(raw) ? Math.min(Math.max(raw, 0), 100) : 0;
    const angle = Math.PI * (1 - value / 100);
    const needleLength = height / 1.40;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(-angle);
    ctx.beginPath();
    ctx.moveTo(0, -10);
    ctx.lineTo(needleLength, 0);
    ctx.lineTo(0, 10);
    ctx.fillStyle = GAUGE_GRADIENT.needle;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, 15, 0, Math.PI * 2);
    ctx.fillStyle = GAUGE_GRADIENT.needle;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, 3, 0, Math.PI * 2);
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 6;
    ctx.stroke();

    ctx.restore();
  },
};

function MainCenterGaugeChart({ dataType, gaugeData }) {

  const isMobile = useIsMobile();

  const ivCapa = useSelector((state) => state.Iv_list.iv_capa);
  const ivData = useSelector((state) => state.ivData || { todayAdd: [] });

  // 🔹 효율 모드 (0: 효율보기, 1: 개별보기) - DB에서 읽어옴
  const [effMode, setEffMode] = useState(0);

  // 상태로 관리
  const [gaugeColor, setGaugeColor] = useState(GAUGE_GRADIENT.accent);
  const [TopTitle, setTopTitle] = useState('발전전력');
  const [BottomTitle, setBottomTitle] = useState('발전전력');
  const [viewData, setViewData] = useState({ value: 0, unit: '' });
  const [gaugeValue, setGaugeValue] = useState(0); // 바늘 위치 0~100

  // 모듈 용량 값을 상태로 관리
  const [totalCapa, setTotalCapa] = useState(0);
  const [totalGrid_Power, setTotalGrid_Power] = useState(0);
  const [totalTodayAdd, setTotaltodayAdd] = useState(0);

  const [Con_Eff, SetCon_Eff] = useState(0);

  //전일 발전량 
  const [prevMonthPower, setprevMonthPower] = useState({ value: 0, unit: 'kWh' });

  // 🔹 효율 모드 조회
  useEffect(() => {
    const fetchEffMode = async () => {
      try {
        const res = await fetch(`${config.backendUrl}/api/get-eff-mode`);
        const data = await res.json();
        if (data && typeof data.mode === 'number') {
          setEffMode(data.mode);  // 0: 효율보기, 1: 개별보기
        }
      } catch (err) {
        console.error('효율 모드 조회 실패:', err);
        // 실패해도 기본값 0(효율보기) 유지
      }
    };
    fetchEffMode();
  }, []);

  // useEffect를 사용하여 iv_capa 값이 변경될 때마다 합산
  useEffect(() => {
    if (ivCapa && ivCapa.length > 0) {
      const sumCapa = ivCapa.reduce((acc, val) => acc + val, 0);
      setTotalCapa(sumCapa);  // totalCapa 상태 업데이트
    }
  }, [ivCapa]);

  // 변환 효율 계산
  useEffect(() => {
    const pvArr   = ivData?.PvPower;
    const gridArr = ivData?.Grid_Power;

    if (!Array.isArray(gridArr)) {
      SetCon_Eff(0);
      return;
    }

    let totalEfficiency = 0;
    let validInverterCount = 0;

    // ✅ 루프 길이는 gridArr 기준으로만 (오버런 방지)
    const N = gridArr.length;

    const vScale = 0.1;   // 0.1 V (OCIP 스케일)
    const iScale = 0.01;  // 0.01 A

    for (let idx = 0; idx < N; idx++) {
      const outputW = Number(gridArr[idx] ?? 0);

      // 1) 기본: PvPower 사용
      let inputW = Number(pvArr?.[idx] ?? 0);

      // 2) PvPower가 0/비정상이면 MPPT V×I 합으로 대체
      if (!(inputW > 0)) {
        const volts = Array.isArray(ivData?.Mppt_Volt?.[idx]) ? ivData.Mppt_Volt[idx] : null;
        const currs = Array.isArray(ivData?.Mppt_Current?.[idx]) ? ivData.Mppt_Current[idx] : null;

        if (volts && currs) {
          const n = Math.min(volts.length, currs.length);
          let sumW = 0;
          for (let ch = 0; ch < n; ch++) {
            const v = Number(volts[ch]) * vScale;
            const i = Number(currs[ch]) * iScale;
            if (Number.isFinite(v) && Number.isFinite(i) && v > 0 && i > 0) sumW += v * i;
          }
          inputW = sumW;
        }
      }

      if (inputW > 0 && Number.isFinite(outputW) && outputW >= 0) {
        let efficiency = (outputW / inputW) * 100;
        if (!Number.isFinite(efficiency) || efficiency < 0) efficiency = 0;
        if (efficiency > 99.9) efficiency = 99.9;

        totalEfficiency += efficiency;
        validInverterCount += 1;
      }
    }

    if (validInverterCount > 0) {
      const averageEfficiency = totalEfficiency / validInverterCount;
      SetCon_Eff(averageEfficiency.toFixed(1));
    } else {
      SetCon_Eff(0);
    }
  }, [ivData.PvPower, ivData.Grid_Power, ivData.Mppt_Volt, ivData.Mppt_Current]);

  // useEffect를 사용하여 Grid_Power 값을 합산
  useEffect(() => {
    if (Array.isArray(ivData.Grid_Power) && ivData.Grid_Power.length > 0) {
      const sumGrid_Power = ivData.Grid_Power.reduce((acc, val) => acc + val, 0);
      setTotalGrid_Power(sumGrid_Power); // totalGrid_Power 상태 업데이트
    } else {
      console.error('ivGrid_Power is not an array or is empty');
    }
  }, [ivData.Grid_Power]);

  useEffect(() => {
    if (ivData.todayAdd && ivData.todayAdd.length > 0) {
      const sumTodayAdd = ivData.todayAdd.reduce((acc, val) => acc + val, 0);
      setTotaltodayAdd(sumTodayAdd);  
    }
  }, [ivData.todayAdd]);

  //전일 발전량 api 코드 
  useEffect(() => {
    if (dataType === 'todayPower') {
      fetch(`${config.backendUrl}/api/prev-month-generation`, { method: "GET" })   
        .then(res => res.json())
        .then(data => {
          if (data.status === "success") {
            setprevMonthPower({ value: data.prev_month_generation, unit: 'kWh' });
          }
        })
        .catch(err => console.error("전일 발전량 API 오류:", err));
    }
  }, [dataType]); 

  // 게이지 모드에 따른 UI 제어
  useEffect(() => {
    // dataType에 따라 상태 값 변경
    if (dataType === 'nowPower') {
      setTopTitle('현재 전력');
      setBottomTitle('현재 전력');
      setGaugeColor(GAUGE_GRADIENT.accent);
      if (totalCapa > 0) {
        const ratio = Math.min(( (totalGrid_Power * 100) / (totalCapa * 1000)), 100);
        setGaugeValue(ratio);
      } else {
        setGaugeValue(0);
      }
      setViewData(formatPower(totalGrid_Power, "Real"));

    } else if (dataType === 'todayPower') {

      setTopTitle('금일 발전량');
      setBottomTitle('금일 누적');
      setGaugeColor(GAUGE_GRADIENT.end);
      setViewData(formatPower(totalTodayAdd, "Add"));

      if (totalCapa > 0) {
        const ratio = Math.min(( (totalTodayAdd * 100) / (totalCapa * 1000 * 5)), 100);
        setGaugeValue(ratio);
      } else {
        setGaugeValue(0);
      }

    } else if (dataType === 'conEff') {
      // 🔹 effMode에 따라 표시 방식을 다르게
      // effMode === 0 : 변환 효율 표시 (기존 화면)
      // effMode === 1 : 개별 인버터 현재전력 표시 (IvCurrentPowerPanel 컴포넌트 사용)
      if (effMode === 0) {
        // ✅ 변환 효율 모드
        setTopTitle('변환 효율');
        setBottomTitle('변환 효율');
        setGaugeColor(GAUGE_GRADIENT.mid);
        setViewData({ value: Con_Eff, unit: '%' });
        if (Con_Eff > 0) {
          setGaugeValue(Math.min(Con_Eff, 100));
        } else {
          setGaugeValue(0);
        }
      }
      // effMode === 1일 때는 IvCurrentPowerPanel 컴포넌트를 렌더링하므로 상태 설정 불필요
    }
  }, [
    dataType,
    totalGrid_Power,
    Con_Eff,
    totalTodayAdd,
    totalCapa,
    effMode,
    ivData.Grid_Power,
    ivCapa,
  ]);

  // 차트 데이터 — 회색 트랙 + 값만큼 파랑→연두 그라데이션이 차오름
  const filled = Math.min(Math.max(Number(gaugeValue) || 0, 0), 100);
  const remaining = 100 - filled;

  const data = {
    labels: ['Used', 'Remaining'],
    datasets: [
      {
        data: [filled, remaining],
        backgroundColor: (context) => {
          if (context.dataIndex === 1) return '#C8D2D4';
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) return GAUGE_GRADIENT.start;
          return createGaugeArcGradient(ctx, chartArea);
        },
        borderWidth: 0,
        cutout: '50%',
        rotation: 270,
        circumference: 180,
      },
    ],
  };

  const options = {
    rotation: -90,
    circumference: 180,
    needleValue: filled,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    maintainAspectRatio: false,
  };

  // effMode === 1이고 dataType === 'conEff'일 때 IvCurrentPowerPanel 렌더링
  if (dataType === 'conEff' && effMode === 1) {
    return <IvCurrentPowerPanel />;
  }

  return (
    <div>
      <div className='chart-container'>
        <div className='gauge-chart-container'>
       
          {/* 도넛박스 */}
          <div className='gauge-chart-midbox'>
            <Container fluid>
              <div className='gauge-chart-doughnut' style={{ backgroundColor: 'var(--card-bg, #ffffff)' }}>
                <Doughnut data={data} options={options} plugins={[needlePlugin]} />
                <div className='gauge-labels' style={{ marginTop: '25px' }}>
                  <span className='gauge-min-label'>Minimum</span>
                  <span className='gauge-max-label'>Maximum</span>
                </div>
              </div>
            </Container>
          </div>
          {/* 하단박스 */}
          <div className='gauge-chart-bottombox'>
            <div className='gauge-chart-bottombox-list'>
              <div
                style={{
                  color: '#000000',
                  fontSize: isMobile ? "16px" : "20px",
                  fontWeight: '700',
                  paddingLeft: '12px',
                  flexShrink: 0,
                }}
              >
                {BottomTitle}
              </div>
              <div
                style={{
                  color: gaugeColor,
                  fontSize: isMobile ? "30px" : "37px",
                  fontWeight: '700',
                  marginLeft: 'auto',
                  paddingRight: '8px',
                }}
              >
                {viewData.value}
              </div>
              <div
                style={{
                  color: gaugeColor,
                  fontSize: '20px',
                  fontWeight: '700',
                  alignSelf: 'flex-end',
                  paddingRight: '12px',
                  paddingBottom: '12px',
                  flexShrink: 0,
                }}
              >
                {viewData.unit}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MainCenterGaugeChart;